/**
 * Checkout Login Guard
 * Ensures users are logged in before they can proceed to checkout
 * Provides a seamless UX without disrupting the shopping experience
 */

class CheckoutLoginGuard {
  constructor() {
    this.modal = null;
    this.overlay = null;
    this.isCustomerLoggedIn = window.Shopify?.customer !== null && window.Shopify?.customer !== undefined;
    this.init();
  }

  init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.modal = document.getElementById('login-required-modal');

    if (!this.modal) {
      console.warn('Login modal not found. Please ensure login-required-modal snippet is included.');
      return;
    }

    this.overlay = this.modal;

    // Expose showModal globally for inline onclick handlers
    window.showLoginModal = () => this.showModal();

    // Setup event listeners
    this.setupCheckoutInterceptors();
    this.setupModalControls();

    // Add analytics tracking
    this.trackLoginPrompts();
  }

  setupCheckoutInterceptors() {
    // Intercept main checkout button in cart
    this.interceptCheckoutButton();

    // Intercept any dynamic checkout buttons (Shop Pay, PayPal, etc.)
    this.interceptDynamicCheckoutButtons();

    // Intercept cart form submission
    this.interceptCartFormSubmission();
  }

  interceptCheckoutButton() {
    const checkoutButton = document.getElementById('checkout');

    if (checkoutButton) {
      checkoutButton.addEventListener('click', (e) => {
        if (!this.isCustomerLoggedIn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.showModal();
          return false;
        }
      }, true); // Use capture phase to intercept before other handlers
    }

    // Also check for any other checkout buttons
    const checkoutButtons = document.querySelectorAll('[name="checkout"], .cart__checkout-button');
    checkoutButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        if (!this.isCustomerLoggedIn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.showModal();
          return false;
        }
      }, true);
    });
  }

  interceptDynamicCheckoutButtons() {
    // Intercept dynamic checkout buttons (Shop Pay, Apple Pay, etc.)
    const observer = new MutationObserver(() => {
      const dynamicButtons = document.querySelectorAll(
        '.shopify-payment-button__button, ' +
        '.additional-checkout-buttons button, ' +
        '[data-shopify-buttoncontainer] button'
      );

      dynamicButtons.forEach(button => {
        // Check if we've already added listener
        if (!button.hasAttribute('data-login-guard')) {
          button.setAttribute('data-login-guard', 'true');

          button.addEventListener('click', (e) => {
            if (!this.isCustomerLoggedIn) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.showModal();
              return false;
            }
          }, true);
        }
      });
    });

    // Observe the cart area for dynamic button additions
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) {
      observer.observe(cartFooter, {
        childList: true,
        subtree: true
      });
    }
  }

  interceptCartFormSubmission() {
    const cartForm = document.getElementById('cart');

    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        // Check if this is a checkout submission (not a cart update)
        const submitter = e.submitter;

        if (submitter && (
          submitter.name === 'checkout' ||
          submitter.id === 'checkout' ||
          submitter.classList.contains('cart__checkout-button')
        )) {
          if (!this.isCustomerLoggedIn) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.showModal();
            return false;
          }
        }
      }, true);
    }
  }

  setupModalControls() {
    // Close button
    const closeButton = this.modal.querySelector('.login-modal-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hideModal());
    }

    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.hideModal();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display !== 'none') {
        this.hideModal();
      }
    });
  }

  showModal() {
    if (!this.modal) return;

    // Show modal with animation
    this.modal.style.display = 'flex';

    // Trigger animation
    requestAnimationFrame(() => {
      this.modal.classList.add('active');
    });

    // Prevent body scroll
    document.body.classList.add('login-modal-open');
    document.body.style.overflow = 'hidden';

    // Focus management for accessibility
    this.trapFocus();

    // Track analytics
    this.trackModalShown();
  }

  hideModal() {
    if (!this.modal) return;

    // Hide with animation
    this.modal.classList.remove('active');

    setTimeout(() => {
      this.modal.style.display = 'none';
    }, 300);

    // Restore body scroll
    document.body.classList.remove('login-modal-open');
    document.body.style.overflow = '';

    // Restore focus
    this.restoreFocus();
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Store current focus to restore later
    this.previouslyFocusedElement = document.activeElement;

    // Focus first element
    firstElement.focus();

    // Trap focus within modal
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.modal.addEventListener('keydown', this.focusTrapHandler);
  }

  restoreFocus() {
    if (this.focusTrapHandler) {
      this.modal.removeEventListener('keydown', this.focusTrapHandler);
    }

    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }

  trackModalShown() {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'login_prompt_shown', {
        event_category: 'checkout',
        event_label: 'Login required for checkout',
        page_location: window.location.href
      });
    }

    // Microsoft Clarity custom event
    if (typeof clarity !== 'undefined') {
      clarity('set', 'login_prompt', 'shown');
    }
  }

  trackLoginPrompts() {
    // Track when users click login or register from modal
    const loginButton = this.modal?.querySelector('a[href*="/account/login"]');
    const registerButton = this.modal?.querySelector('a[href*="/account/register"]');

    if (loginButton) {
      loginButton.addEventListener('click', () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'login_clicked', {
            event_category: 'checkout',
            event_label: 'User clicked login from modal'
          });
        }
      });
    }

    if (registerButton) {
      registerButton.addEventListener('click', () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'register_clicked', {
            event_category: 'checkout',
            event_label: 'User clicked register from modal'
          });
        }
      });
    }
  }
}

// Initialize the guard
if (typeof window !== 'undefined') {
  window.checkoutLoginGuard = new CheckoutLoginGuard();
}
